import { useState } from 'react';
import {
  Button,
  List,
  ListItem,
  ListItemText,
  Box,
  Stack,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Chip,
} from '@mui/material';
import Loading from '../../components/loading';
import SuccessMessage from '../../components/successMessage';
import ErrorMessage from '../../components/errorMessage';
import { WhatsApp } from '@mui/icons-material';
import { CircleCheckBig } from 'lucide-react';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { useProfessionals } from '../../hooks/useProfessionals';
import { useAppointmentsToConfirm } from '../hooks/useAppointmentsToConfirm';
import { useAppointmentActions } from '../hooks/useAppointmentActions';

export default function AppointmentListToConfirm() {
  const [isLoading, setLoading] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [selectedProfessionalId, setSelectedProfessionalId] = useState('99');

  const [startDate, setStartDate] = useState(dayjs());
  const [endDate, setEndDate] = useState(dayjs());

  const { professionals: dentists } = useProfessionals();
  const {
    appointments: allAppointmentsToConfirm,
    isLoading: isListLoading,
    refetch: refetchAppointmentsToConfirm,
  } = useAppointmentsToConfirm({
    startDate,
    endDate,
    professionalId: selectedProfessionalId !== '99' ? selectedProfessionalId : null,
  });
  const { confirm } = useAppointmentActions();

  const handleWhatsAppClick = (phoneNumber) => {
    const phoneNumberFormatted = phoneNumber.replace(/\D/g, '');
    const fullPhoneNumber = `55${phoneNumberFormatted}`;

    window.open(`https://web.whatsapp.com/send/?phone=${fullPhoneNumber}`, '_blank');
  };

  const confirmAppointment = (appointmentId) => {
    setLoading(true);
    confirm(appointmentId)
      .then((returnFromApi) => {
        setLoading(false);
        setShowSuccessMessage(true);
        setSuccessMessage(returnFromApi.data);
        refetchAppointmentsToConfirm();
      })
      .catch((returnFromApi) => {
        setLoading(false);
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
        console.error(returnFromApi);
      });
  };

  const dentistChange = (event) => {
    setSelectedProfessionalId(event.target.value);
  };

  return (
    <Card sx={{ borderRadius: '1rem', boxShadow: 3 }}>
      <CardHeader
        title={
          <Typography variant="h5" sx={{ mb: 0, fontWeight: 'bold', color: 'text.secondary' }}>
            Consultas
          </Typography>
        }
        subheader={
          <Typography variant="body2" color="text.secondary">
            Aqui aparecem apenas as consultas que estão pendentes de confirmação.
          </Typography>
        }
        sx={{ borderBottom: 0, py: 3 }}
      />
      <CardContent>
        <Loading isToHidden={!isLoading && !isListLoading} />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="dentist-select-label">Selecione o Profissional</InputLabel>
            <Select
              labelId="dentist-select-label"
              id="dentist-select"
              value={selectedProfessionalId}
              label="Selecione o Profissional"
              onChange={dentistChange}
            >
              <MenuItem value="99">
                <em>Todos os profissionais</em>
              </MenuItem>
              {dentists.length === 0 && (
                <MenuItem disabled value="">
                  Nenhum profissional encontrado
                </MenuItem>
              )}
              {dentists.map((dentist) => (
                <MenuItem key={dentist.id} value={dentist.id}>
                  {dentist.nome || dentist.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <DatePicker
            label={'Data inicial'}
            value={startDate}
            onChange={(date) => setStartDate(date)}
            format="DD/MM/YYYY"
          />

          <DatePicker
            label={'Data final'}
            value={endDate}
            onChange={(date) => setEndDate(date)}
            format="DD/MM/YYYY"
          />
        </Stack>

        {showSuccessMessage && (
          <SuccessMessage
            message={successMessage}
            show={showSuccessMessage}
            setShow={setShowSuccessMessage}
          />
        )}
        {showErrorMessage && (
          <ErrorMessage
            message={errorMessage}
            show={showErrorMessage}
            setShow={setShowErrorMessage}
          />
        )}

        <List>
          {allAppointmentsToConfirm.length > 0 ? (
            allAppointmentsToConfirm.map((appointment) => (
              <ListItem
                data-cy="appointment-row"
                key={appointment.appointmentId}
                sx={{
                  mt: 1,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  p: 1,
                  boxShadow: 1,
                }}
              >
                <ListItemText
                  primary={appointment.patientName}
                  secondary={
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5 }}
                    >
                      <span>{appointment.formattedDate}</span>
                    </Stack>
                  }
                />

                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  sx={{ alignItems: { sm: 'center' } }}
                >
                  {appointment.sentToConfirm && (
                    <Chip
                      label="Confirmação solicitada via sistema"
                      size="small"
                      variant="outlined"
                      color="info"
                      sx={{
                        borderRadius: 999,
                        height: 22,
                        '& .MuiChip-label': { px: 1, fontSize: 12 },
                      }}
                    />
                  )}

                  <Tooltip
                    disabled={appointment.patientPhoneNumber}
                    title={'Paciente sem celular cadastrado'}
                    disableHoverListener={appointment.patientPhoneNumber}
                    disableFocusListener={appointment.patientPhoneNumber}
                    disableTouchListener={appointment.patientPhoneNumber}
                  >
                    <span style={{ display: 'inline-flex' }}>
                      <Button
                        variant="outlined"
                        color="success"
                        onClick={() => handleWhatsAppClick(appointment.patientPhoneNumber)}
                        startIcon={<WhatsApp />}
                        disabled={!appointment.patientPhoneNumber}
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        Chamar no WhatsApp
                      </Button>
                    </span>
                  </Tooltip>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={() => confirmAppointment(appointment.appointmentId)}
                    startIcon={<CircleCheckBig />}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Confirmar
                  </Button>
                </Stack>
              </ListItem>
            ))
          ) : (
            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Typography>
                Nenhuma consulta pendente de confirmação para o período selecionado.
              </Typography>
            </Box>
          )}
        </List>
      </CardContent>
    </Card>
  );
}
